import React from 'react';
import { Link } from 'react-router-dom';
import { clickableLinks } from '../../styles/colors';

interface LegalContentRendererProps {
  content: string;
}

interface SectionType {
  number?: string;
  title?: string;
  content: string;
}

const slugifySectionTitle = (title: string): string =>
  title
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

/**
 * Component that renders legal content text with proper formatting:
 * - Preserves line breaks
 * - Converts email addresses to mailto links
 * - Converts URLs to external links
 * - Converts "page de contact" references to internal links
 * - Formats numbered sections
 * - Formats bullet lists (lines starting with "- ")
 * - Uniform spacing and formatting
 */
const LegalContentRenderer: React.FC<LegalContentRendererProps> = ({ content }) => {
  const processText = (text: string): React.ReactNode[] => {
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;

    // Regex patterns
    const emailPattern = /([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)/g;
    const urlPattern = /(https?:\/\/[^\s]+)/g;
    const contactPagePattern = /(page de contact|contact page)/gi;

    // Find all matches with their positions
    const matches: Array<{ start: number; end: number; type: 'email' | 'url' | 'contact'; text: string }> = [];

    let match;
    while ((match = emailPattern.exec(text)) !== null) {
      matches.push({ start: match.index, end: match.index + match[0].length, type: 'email', text: match[0] });
    }
    emailPattern.lastIndex = 0;

    while ((match = urlPattern.exec(text)) !== null) {
      matches.push({ start: match.index, end: match.index + match[0].length, type: 'url', text: match[0] });
    }
    urlPattern.lastIndex = 0;

    while ((match = contactPagePattern.exec(text)) !== null) {
      matches.push({ start: match.index, end: match.index + match[0].length, type: 'contact', text: match[0] });
    }
    contactPagePattern.lastIndex = 0;

    // Sort matches by position
    matches.sort((a, b) => a.start - b.start);

    // Process matches and text
    matches.forEach((match) => {
      // Add text before match
      if (match.start > lastIndex) {
        parts.push(text.substring(lastIndex, match.start));
      }

      // Add link
      if (match.type === 'email') {
        parts.push(
          <a key={`${match.start}-email`} href={`mailto:${match.text}`} className={clickableLinks}>
            {match.text}
          </a>
        );
      } else if (match.type === 'url') {
        parts.push(
          <a
            key={`${match.start}-url`}
            href={match.text}
            target="_blank"
            rel="noopener noreferrer"
            className={clickableLinks}
          >
            {match.text}
          </a>
        );
      } else if (match.type === 'contact') {
        parts.push(
          <Link key={`${match.start}-contact`} to="/contact" className={clickableLinks}>
            {match.text}
          </Link>
        );
      }

      lastIndex = match.end;
    });

    // Add remaining text
    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }

    return parts.length > 0 ? parts : [text];
  };

  const renderContent = (contentText: string): React.ReactNode => {
    const lines = contentText.split('\n');
    const elements: React.ReactNode[] = [];
    let currentParagraph: string[] = [];
    let currentList: string[] = [];

    const flushParagraph = () => {
      if (currentParagraph.length > 0) {
        // If multiple lines, render each as separate paragraph
        if (currentParagraph.length > 1) {
          currentParagraph.forEach((line, idx) => {
            elements.push(
              <p key={`para-${elements.length}-${idx}`} className="text-gray-700 leading-relaxed mb-2">
                {processText(line)}
              </p>
            );
          });
        } else {
          elements.push(
            <p key={`para-${elements.length}`} className="text-gray-700 leading-relaxed mb-4">
              {processText(currentParagraph[0])}
            </p>
          );
        }
        currentParagraph = [];
      }
    };

    const flushList = () => {
      if (currentList.length > 0) {
        elements.push(
          <ul key={`list-${elements.length}`} className="list-disc list-inside ml-4 mb-4 space-y-1 text-gray-700">
            {currentList.map((item, idx) => (
              <li key={idx} className="leading-relaxed">
                {processText(item)}
              </li>
            ))}
          </ul>
        );
        currentList = [];
      }
    };

    for (const line of lines) {
      const trimmed = line.trim();
      
      if (!trimmed) {
        flushList();
        flushParagraph();
        continue;
      }

      // Check if line starts with "- " (bullet list item)
      if (trimmed.startsWith('- ')) {
        flushParagraph();
        const listItem = trimmed.substring(2).trim(); // Remove "- " prefix
        currentList.push(listItem);
      } else {
        flushList();
        currentParagraph.push(trimmed);
      }
    }

    // Flush remaining content
    flushList();
    flushParagraph();

    return elements.length > 0 ? <>{elements}</> : null;
  };

  // Split content into sections (numbered sections)
  const lines = content.split('\n');
  const sections: SectionType[] = [];
  let currentSection: SectionType | null = null;

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) {
      if (currentSection) {
        currentSection.content += '\n';
      }
      continue;
    }

    // Check if line starts with a number (section heading)
    const sectionMatch = trimmed.match(/^(\d+)\.\s+(.+)$/);
    if (sectionMatch) {
      // Save previous section
      if (currentSection) {
        sections.push({
          number: currentSection.number,
          title: currentSection.title,
          content: currentSection.content.trim(),
        });
      }
      // Start new section
      currentSection = {
        number: sectionMatch[1],
        title: sectionMatch[2],
        content: '',
      };
    } else if (currentSection) {
      // Add line to current section
      currentSection.content += (currentSection.content ? '\n' : '') + trimmed;
    } else {
      // Content before first section
      if (sections.length === 0) {
        sections.push({ content: trimmed });
      } else {
        sections[sections.length - 1].content += '\n' + trimmed;
      }
    }
  }

  // Add last section
  if (currentSection) {
    const lastSection: SectionType = {
      number: currentSection.number,
      title: currentSection.title,
      content: currentSection.content.trim(),
    };
    sections.push(lastSection);
  }

  return (
    <div className="prose prose-gray max-w-none">
      {sections.map((section, index) => {
        if (section.number && section.title) {
          const sectionId = slugifySectionTitle(section.title);

          return (
            <section key={index} id={sectionId} className="mb-8 scroll-mt-24 first:mt-0">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {section.number}. {section.title}
              </h2>
              <div className="text-gray-700 leading-relaxed">
                {renderContent(section.content)}
              </div>
            </section>
          );
        }

        // Regular paragraph (no section number) - for intro content
        return (
          <div key={index} className="mb-6 text-gray-700 leading-relaxed">
            {renderContent(section.content)}
          </div>
        );
      })}
    </div>
  );
};

export default LegalContentRenderer;